import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  HardHat, Trash2, Eye, Pencil, X, Loader2, Search, Calendar,
  Building2, AlertTriangle, RotateCcw, Clock, CheckCircle2, FileSpreadsheet, Users, Handshake
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import ImportObraModal from "./ImportObraModal";
// Fix AU (2026-09-06): convocatoria diaria de personal por obra.
import { ConvocatoriaModal } from "./ConvocatoriaModal";
import { TercerizadosGlobalModal } from "./TercerizadosGlobal"; // Fix CJ (2026-09-11)

const opts = { withCredentials: true };
const MANAGE_ROLES = ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica"];

const STATUS_BADGE = {
  activa:     { label: "Activa",     cls: "bg-emerald-100 text-emerald-700" },
  pausada:    { label: "Pausada",    cls: "bg-amber-100 text-amber-700" },
  finalizada: { label: "Finalizada", cls: "bg-blue-100 text-blue-700" },
  cancelada:  { label: "Cancelada",  cls: "bg-red-100 text-red-700" },
};
const TABS = [
  ["todas", "Todas"], ["activa", "Activas"], ["pausada", "Pausadas"],
  ["finalizada", "Finalizadas"], ["cancelada", "Canceladas"],
];

// ─── EDIT MODAL ──────────────────────────────────────────────────────────
function EditObraModal({ obra, onClose, onSaved }) {
  const [nombre, setNombre] = useState(obra?.nombre || "");
  const [status, setStatus] = useState(obra?.status || "activa");
  const [saving, setSaving] = useState(false);
  if (!obra) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/obras/${obra._id}`, { nombre, status }, opts);
      toast.success("Obra actualizada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="obra-edit-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Editar Obra</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Nombre</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C]" data-testid="obra-edit-nombre" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Estado</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C]" data-testid="obra-edit-status">
              {Object.keys(STATUS_BADGE).map(s => <option key={s} value={s}>{STATUS_BADGE[s].label}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={save} disabled={saving} className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="obra-edit-save">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>, document.body);
}

// ─── PAPELERA MODAL ────────────────────────────────────────────────────────
function PapeleraModal({ onClose, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/obras/papelera`, opts)
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Error cargando papelera"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const restore = async (id) => {
    try { await axios.post(`${API}/obras/${id}/restore`, {}, opts); toast.success("Obra restaurada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const purge = async (id) => {
    if (!window.confirm("Eliminar definitivamente esta obra? No se puede deshacer.")) return;
    try { await axios.delete(`${API}/obras/${id}/permanent`, opts); toast.success("Eliminada definitivamente"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="obra-papelera-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Trash2 size={18} className="text-gray-500" /> Papelera de Obras</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6">
          {loading ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            : items.length === 0 ? (
              <div className="text-center py-10 text-gray-400"><Trash2 size={36} className="mx-auto mb-2 text-gray-200" /><p>La papelera está vacía</p></div>
            ) : (
              <div className="space-y-2">
                {items.map(o => (
                  <div key={o._id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100" data-testid={`obra-papelera-row-${o._id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate">{o.nombre}</p>
                      <p className="text-xs text-gray-500 font-mono">{o.codigo} · {o.cliente_nombre}</p>
                    </div>
                    {canManage && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => restore(o._id)} className="flex items-center gap-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-lg text-xs font-medium" data-testid={`obra-restore-btn-${o._id}`}><RotateCcw size={13} /> Restaurar</button>
                        <button onClick={() => purge(o._id)} className="flex items-center gap-1 border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-medium" data-testid={`obra-purge-btn-${o._id}`}><Trash2 size={13} /> Eliminar</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>, document.body);
}

// ─── DELETE CONFIRM ──────────────────────────────────────────────────────
function DeleteConfirm({ obra, onClose, onConfirm }) {
  if (!obra) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="obra-delete-modal">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-red-50 text-red-600"><AlertTriangle size={20} /></div>
            <h2 className="text-lg font-bold text-gray-900">Eliminar Obra</h2>
          </div>
          <p className="text-sm text-gray-600">¿Enviar <strong>{obra.nombre}</strong> ({obra.codigo}) a la papelera? Podrás restaurarla durante 30 días.</p>
          <div className="flex gap-3 pt-5">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="obra-delete-confirm">Eliminar</button>
          </div>
        </div>
      </div>
    </div>, document.body);
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
export default function ObraList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todas");
  const [search, setSearch] = useState("");
  const [showPapelera, setShowPapelera] = useState(false);
  const [showConvocatoria, setShowConvocatoria] = useState(false);   // Fix AU (2026-09-06)
  const [showTercerizados, setShowTercerizados] = useState(false);   // Fix CJ (2026-09-11)
  const [showImport, setShowImport] = useState(false);
  const [editObra, setEditObra] = useState(null);
  const [deleteObra, setDeleteObra] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/obras`, opts)
      .then(({ data }) => setObras(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Error cargando obras"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/obras/${deleteObra._id}`, opts);
      toast.success("Obra enviada a papelera");
      setDeleteObra(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const upcoming = useMemo(() => {
    return obras
      .filter(o => o.fecha_estimada_fin && !["finalizada", "cancelada"].includes(o.status))
      .sort((a, b) => String(a.fecha_estimada_fin).localeCompare(String(b.fecha_estimada_fin)))
      .slice(0, 5);
  }, [obras]);

  const filtered = obras.filter(o => {
    const okTab = tab === "todas" || o.status === tab;
    const okSearch = !search ||
      (o.nombre || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.codigo || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.cliente_nombre || "").toLowerCase().includes(search.toLowerCase());
    return okTab && okSearch;
  });

  const counts = obras.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="obra-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Obra</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Obra</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control de ejecución, personal, avance de campo y centros de costo</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {canManage && (
            <button onClick={() => setShowImport(true)} className="flex items-center gap-2 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="obra-import-btn">
              <FileSpreadsheet size={15} /> Importar Excel
            </button>
          )}
          {canManage && (
            <button onClick={() => setShowTercerizados(true)} className="flex items-center gap-2 border border-orange-200 text-[#EA580C] hover:bg-orange-50 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="obra-tercerizados-btn">
              <Handshake size={15} /> Tercerizados
            </button>
          )}
          {/* Fix AU (2026-09-06): la convocatoria del día siguiente. */}
          <button onClick={() => setShowConvocatoria(true)} className="flex items-center gap-2 border border-orange-200 text-[#EA580C] hover:bg-orange-50 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="obra-convocatoria-btn">
            <Users size={15} /> Convocatoria
          </button>
          <button onClick={() => setShowPapelera(true)} className="flex items-center gap-2 border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="obra-papelera-btn">
            <Trash2 size={15} /> Papelera
          </button>
        </div>
      </div>

      {/* Próximas obras a finalizar */}
      {upcoming.length > 0 && (
        <div className="bg-orange-50/70 rounded-2xl border border-orange-100 p-4" data-testid="upcoming-obras-block">
          <p className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Clock size={13} /> Próximas Obras a Finalizar</p>
          <div className="space-y-1.5">
            {upcoming.map(o => (
              <div key={o._id} className="flex items-center justify-between gap-3 bg-white rounded-xl px-3 py-2 border border-orange-100" data-testid={`upcoming-obra-row-${o._id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{o.nombre}</p>
                  <p className="text-xs text-gray-500 font-mono">{o.codigo} · {o.cliente_nombre}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={12} />{formatDate(o.fecha_estimada_fin)}</span>
                  <button onClick={() => navigate(`/obra/${o._id}`)} className="text-xs font-semibold text-[#EA580C] hover:text-[#C2410C]">Ver</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${tab === key ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-300 hover:border-[#EA580C]/40"}`} data-testid={`obra-status-tab-${key}`}>
              {label}{key !== "todas" && counts[key] ? ` (${counts[key]})` : ""}
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar obra..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C] bg-white" data-testid="obra-search-input" />
        </div>
      </div>

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200/60">
          <HardHat size={42} className="mb-3 text-gray-200" />
          <p className="font-medium text-gray-500">No hay obras en este estado</p>
          <p className="text-xs text-gray-400 mt-1">Las obras se crean automáticamente al aprobar un presupuesto de tipo Obra.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="obra-cards-grid">
          {filtered.map(o => {
            const badge = STATUS_BADGE[o.status] || { label: o.status, cls: "bg-gray-100 text-gray-600" };
            return (
              <div key={o._id} className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`obra-card-${o._id}`}>
                <div className="flex items-start justify-between mb-3 gap-2">
                  <span className="font-mono text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg border border-gray-200">{o.codigo}</span>
                  <span className="flex items-center gap-1.5">
                    {/* Fix V (2026-09-02): progreso de la actualización 2.0 (resumen guardado por GET activacion-2-0 en la clave estado_2_0, sin "iva" en el nombre para que sobreviva al scrub del Residente) */}
                    {o.estado_2_0
                      ? <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${o.estado_2_0.listos >= o.estado_2_0.pasos ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`obra-chip-20-${o._id}`}>2.0 · {o.estado_2_0.listos}/{o.estado_2_0.pasos}</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-500" data-testid={`obra-chip-20-${o._id}`}>2.0 · sin revisar</span>}
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 text-base leading-snug mb-1 line-clamp-2">{o.nombre}</h3>
                <p className="text-sm text-gray-500 mb-3 flex items-center gap-1.5"><Building2 size={13} />{o.cliente_nombre || "Sin cliente"}</p>
                <div className="border-t border-gray-100 pt-3 space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Presupuesto base</span><span className="font-mono text-gray-700">{o.presupuesto_base_number || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Ítems</span><span className="font-semibold text-gray-800">{(o.items || []).length}</span></div>
                  {/* Fix N (2026-08-31): desde el fix M el Residente ya no recibe `total`,
                      y la tarjeta le mostraba "₲ 0", que da a entender que la obra no vale
                      nada. Si el campo no viene, la fila no se dibuja. */}
                  {o.total != null && <div className="flex justify-between"><span className="text-gray-500">Monto total (IVA incl.)</span><span className="font-bold text-gray-900">{formatPYG(o.total)}</span></div>}
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => navigate(`/obra/${o._id}`)} className="flex-1 flex items-center justify-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white py-2 rounded-xl text-xs font-semibold" data-testid={`obra-ver-btn-${o._id}`}><Eye size={14} /> Ver</button>
                  {canManage && <button onClick={() => setEditObra(o)} className="flex items-center justify-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-xs font-semibold" data-testid={`obra-editar-btn-${o._id}`}><Pencil size={14} /></button>}
                  {canManage && <button onClick={() => setDeleteObra(o)} className="flex items-center justify-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 px-3 py-2 rounded-xl text-xs font-semibold" data-testid={`obra-eliminar-btn-${o._id}`}><Trash2 size={14} /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showPapelera && <PapeleraModal onClose={() => { setShowPapelera(false); load(); }} canManage={canManage} />}
      <ConvocatoriaModal open={showConvocatoria} onClose={() => setShowConvocatoria(false)} />
      {showTercerizados && <TercerizadosGlobalModal onClose={() => setShowTercerizados(false)} />}
      {showImport && <ImportObraModal onClose={() => setShowImport(false)} onImported={load} />}
      {editObra && <EditObraModal obra={editObra} onClose={() => setEditObra(null)} onSaved={load} />}
      {deleteObra && <DeleteConfirm obra={deleteObra} onClose={() => setDeleteObra(null)} onConfirm={confirmDelete} />}
    </div>
  );
}
